import { getGoogleClientId } from './googleClientId';

const pad = n => String(n).padStart(2, '0');

// UTC com Z — único formato que o app Android do Google Calendar lê corretamente
const formatGCalDate = (d) =>
  `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;

const formatICSDate = (d) =>
  d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

// Tenta token silencioso (funciona se o usuário já autorizou antes)
export const requestCalendarTokenSilent = () => {
  const clientId = getGoogleClientId();
  return new Promise((resolve) => {
    if (!window.google?.accounts?.oauth2 || !clientId) { resolve(null); return; }
    try {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'https://www.googleapis.com/auth/calendar.events',
        prompt: 'none',
        callback: (r) => resolve(r.error ? null : r.access_token),
        error_callback: () => resolve(null)
      });
      client.requestAccessToken({ prompt: 'none' });
    } catch { resolve(null); }
  });
};

// Pede token com popup (deve ser chamado ANTES de qualquer await, dentro de um click handler)
// O client_id é lido do cache síncrono — prefetchGoogleClientId() deve ter sido chamado antes
export const requestCalendarToken = () => {
  const clientId = getGoogleClientId();
  return new Promise((resolve) => {
    if (!window.google?.accounts?.oauth2 || !clientId) { resolve(null); return; }
    try {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'https://www.googleapis.com/auth/calendar.events',
        callback: (r) => resolve(r.error ? null : r.access_token),
        error_callback: () => resolve(null)
      });
      client.requestAccessToken({ prompt: '' });
    } catch { resolve(null); }
  });
};

export const createCalendarEvent = async (accessToken, vaga, user = null) => {
  if (!accessToken || !vaga?.data_hora) return null;
  try {
    const start  = new Date(vaga.data_hora);
    const end    = new Date(start.getTime() + 60 * 60 * 1000);
    const aula   = vaga.descricao || 'Pilates';
    const nome   = user?.name || user?.nome || '';
    const email  = user?.email || '';

    const horaBR = start.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
    const dataBR = start.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Sao_Paulo' });

    const linhas = [
      '📍 Allma Pilates',
      `📅 ${dataBR} às ${horaBR}`,
      `🏋️ Aula: ${aula}`,
      '',
    ];
    if (nome) {
      linhas.push(`👥 Aluno(s) (1):`);
      linhas.push(`  • ${nome}`);
      if (email) linhas.push(`    ✉️  ${email}`);
    }
    linhas.push('', 'Status: ✅ Ocupada', '🔗 agenda.allmapilates.com.br');

    const event = {
      summary:     nome ? `🤸 ${aula} · ${nome}` : `🤸 ${aula}`,
      description: linhas.join('\n'),
      location:    'Allma Pilates',
      start:   { dateTime: start.toISOString(), timeZone: 'America/Sao_Paulo' },
      end:     { dateTime: end.toISOString(),   timeZone: 'America/Sao_Paulo' },
      colorId: '2',
      reminders: { useDefault: false, overrides: [{ method: 'popup', minutes: 30 }] },
    };

    const res = await fetch(
      'https://www.googleapis.com/calendar/v3/calendars/primary/events',
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(event),
      }
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data.id;
  } catch { return null; }
};

export const deleteCalendarEvent = async (accessToken, eventId) => {
  if (!accessToken || !eventId) return;
  try {
    await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}`,
      { method: 'DELETE', headers: { 'Authorization': `Bearer ${accessToken}` } }
    );
  } catch { /* silencioso */ }
};

// Link clássico do Google Calendar — único formato reconhecido pelo app Android
export const generateCalendarLink = (vaga, studioName = 'Allma Pilates') => {
  if (!vaga?.data_hora) return null;
  const start = new Date(vaga.data_hora);
  const end   = new Date(start.getTime() + 60 * 60 * 1000);

  const aula = vaga.descricao || 'Pilates';

  const horaBR = start.toLocaleTimeString('pt-BR', {
    hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo'
  });
  const dataBR = start.toLocaleDateString('pt-BR', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
    timeZone: 'America/Sao_Paulo'
  });
  const dataCapitalizada = dataBR.charAt(0).toUpperCase() + dataBR.slice(1);

  const details = [
    `🤸‍♀️ ${aula}`,
    `📍 ${studioName}`,
    `📅 ${dataCapitalizada}`,
    `🕐 ${horaBR} — duração 1 hora`,
    '',
    `✅ Agendamento confirmado!`,
    `🌐 agenda.allmapilates.com.br`,
  ].join('\n');

  const params = new URLSearchParams({
    action:   'TEMPLATE',
    text:     `🤸‍♀️ ${aula} · ${studioName}`,
    dates:    `${formatGCalDate(start)}/${formatGCalDate(end)}`,
    details,
    location: studioName,
    sf:       'true',
    output:   'xml'
  });
  return `https://www.google.com/calendar/event?${params.toString()}`;
};

// Fallback mobile: baixa arquivo .ics
export const downloadICS = (vaga) => {
  if (!vaga?.data_hora) return;
  const start = new Date(vaga.data_hora);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Allma Pilates//PT',
    'BEGIN:VEVENT',
    `DTSTART:${formatICSDate(start)}`,
    `DTEND:${formatICSDate(end)}`,
    `SUMMARY:Pilates – ${vaga.descricao || 'Allma Pilates'}`,
    `DESCRIPTION:Aula de Pilates na Allma Pilates${vaga.secao ? ` | Seção: ${vaga.secao}` : ''}`,
    'LOCATION:Allma Pilates',
    `UID:${Date.now()}@allmapilates.com.br`,
    'END:VEVENT', 'END:VCALENDAR'
  ].join('\r\n');
  const blob = new Blob([lines], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'aula-pilates.ics';
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
};

export const isMobileDevice = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
